import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { Paged } from '@core/api/masters';
import type { CashAccountChoice } from '@core/api/cash';
import {
  saleAccountsPath,
  saleCounterPath,
  saleDayPath,
  saleGarmentsPath,
  saleItemSuggestionsPath,
  saleInvoicePath,
  saleInvoicesPath,
} from '@core/api/queryPaths';
import type {
  SaleCounter,
  SaleDay,
  SaleGarmentQuery,
  SaleItemSuggestion,
  SaleInvoice,
  SaleInvoiceFilters,
  SaleInvoiceSummary,
  SalePreview,
  SaveSaleInput,
  SellableGarment,
} from '@core/api/saleInvoices';

export type {
  SaleDay,
  SaleInvoice,
  SaleInvoiceSummary,
  SaleItemSuggestion,
  SalePreview,
  SellableGarment,
} from '@core/api/saleInvoices';

export const saleInvoicesKey = ['sale-invoice'] as const;

export function useSaleInvoices(branchId: number | undefined, filters: SaleInvoiceFilters = {}) {
  return useQuery({
    queryKey: [...saleInvoicesKey, { branchId, ...filters }],
    queryFn: () => apiFetch<Paged<SaleInvoiceSummary>>(saleInvoicesPath(filters)),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useSaleInvoice(id: number | null) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'one', id],
    queryFn: () => apiFetch<SaleInvoice>(saleInvoicePath(id ?? 0)),
    enabled: id !== null,
  });
}

/** Garments to sell for what is typed — by short name and digits, or by name. Off until something is typed. */
export function useSaleGarments(branchId: number | undefined, query: SaleGarmentQuery | null) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'garments', branchId, query],
    queryFn: () => apiFetch<SellableGarment[]>(saleGarmentsPath(query ?? {})),
    enabled: branchId !== undefined && query !== null,
    placeholderData: (previous) => previous,
  });
}

/** Other items sold before whose name holds what is typed. */
export function useSaleItemSuggestions(branchId: number | undefined, search: string | null) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'item-suggestions', branchId, search],
    queryFn: () => apiFetch<SaleItemSuggestion[]>(saleItemSuggestionsPath(search ?? '')),
    enabled: branchId !== undefined && search !== null,
    placeholderData: (previous) => previous,
  });
}

/** One day's selling — the figures over Today's sales. */
export function useSaleDay(branchId: number | undefined, date: string) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'day', branchId, date],
    queryFn: () => apiFetch<SaleDay>(saleDayPath(date)),
    enabled: branchId !== undefined,
  });
}

/** The accounts a sale's money may go into — names and kinds only (canvas "Money in, by account"). */
export function useSaleAccounts(branchId: number | undefined) {
  return useQuery({
    queryKey: ['cash', 'paid-from', 'sale', { branchId }],
    queryFn: () => apiFetch<CashAccountChoice[]>(saleAccountsPath()),
    enabled: branchId !== undefined,
  });
}

export function useSaleCounter(branchId: number | undefined) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'counter', branchId],
    queryFn: () => apiFetch<SaleCounter>(saleCounterPath()),
    enabled: branchId !== undefined,
  });
}

/**
 * What the cart comes to, asked of the server each time it changes — the client never computes tax.
 * A refusal (no rate, a ceiling breached) comes back as the error, worded by the server.
 */
export function useSalePreview(input: SaveSaleInput | null) {
  return useQuery({
    queryKey: [...saleInvoicesKey, 'preview', input],
    queryFn: () =>
      apiFetch<SalePreview>('/api/sale-invoice/preview', {
        method: 'POST',
        body: { ...input, payments: [] },
      }),
    enabled: input !== null,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export {
  findSellableGarment,
  createSale,
  takeSalePayment,
  cancelSale,
} from '@core/api/saleCommands';
